import React, {useState, useEffect} from "react";
import {useNavigate, useParams, useLocation} from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Breadcrumbs,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import {
  PickerSearch,
  FormDatePicker,
  FormInput,
  Spinner,
  CustomizedSnackbars,
} from "../../../../components";
import {tableHeadAddStockOpname} from "../../../../utils/Constants";
import NumberFormat from "../../../../helpers/functions/numberFormat";

// Import hooks
import {useStockOpnameData} from "../../hooks/useStockOpnameData";
import {useBranches} from "../../hooks/useBranches";
import api from "../.././../../helpers/api";

// Import utilities
import {
  validateStockOpnameForm,
  validateProductChanges,
  prepareProductsForSave,
  calculateNegativeDiffSummary,
  calculatePositiveDiffSummary,
  calculateSummaryData,
} from "../../utils/validation";
import moment from "moment";

function EditStockOpname() {
  const navigate = useNavigate();
  const {id} = useParams();
  const location = useLocation();
  const stockOpnameParams = location.state?.stockOpnameParams;

  // Hooks
  const {
    isSpinner,
    setIsSpinner,
    error,
    stockOpnameProductList,
    setStockOpnameProductList,
    saveStockOpname,
    syncSaveStockOpname,
    handleQuantityChange,
    calculateDifference,
    calculateTotalPrice,
  } = useStockOpnameData(0, 10, true);

  const {
    branchList,
    searchBranchName,
    setSearchBranchName,
    branchSelected,
    setBranchSelected,
  } = useBranches();

  // Local state
  const [isLoading, setIsLoading] = useState(false);
  const [dateStockOpname, setDateStockOpname] = useState(null);
  const [notes, setNotes] = useState("");
  const [errors, setErrors] = useState({});
  const [pageStokOpname, setPageStockOpname] = useState(0);
  const [rowsPerPageStockOpname, setRowsPerPageStockOpname] = useState(10);
  const [draftId, setDraftId] = useState(null);
  const [stockOpnameData, setStockOpnameData] = useState(null);

  // Alert state
  const [isOpen, setIsOpen] = useState(false);
  const [alertType, setAlertType] = useState("");
  const [message, setMessage] = useState("");

  // Saat komponen dimuat, ambil data draft
  useEffect(() => {
    if (stockOpnameParams) {
      // Set data ke state lokal
      setStockOpnameData(stockOpnameParams);

      // Set tanggal dari transactionDate
      if (stockOpnameParams.transactionDate) {
        setDateStockOpname(moment(stockOpnameParams.transactionDate));
      }

      // Set branch berdasarkan branchCode dan branchName
      if (stockOpnameParams.branchCode && stockOpnameParams.branchName) {
        // Cari branch dari branchList, jika ada
        const branch = branchList.find(
          (b) => b.branchCode === stockOpnameParams.branchCode
        );

        if (branch) {
          setBranchSelected(branch);
        } else {
          // Jika tidak ditemukan di branchList, buat objek branch baru
          setBranchSelected({
            branchCode: stockOpnameParams.branchCode,
            branchName: stockOpnameParams.branchName,
          });
        }
      }

      // Set notes jika diperlukan (jika ada field notes di params)
      if (stockOpnameParams.notes) {
        setNotes(stockOpnameParams.notes);
      }

      // Jika ada ID, set untuk digunakan saat update
      if (stockOpnameParams.id) {
        setDraftId(stockOpnameParams.id);

        // Load produk menggunakan ID dan branchCode
        loadDraftData(stockOpnameParams.id, stockOpnameParams.branchCode);
      }
    }
  }, [stockOpnameParams, branchList]);

  // Fungsi untuk memuat dan memproses data draft
  const loadDraftData = async (draftId, branchCode) => {
    console.log("Loading draft data for ID:", draftId);
    console.log("Branch code:", branchCode);
    setIsLoading(true);
    try {
      // const result = await api.getStockOpnameDraft(draftId, branchCode);
      const result = await api.getStockOpnameDraftNew(draftId, branchCode);

      if (result.statusCode === 200) {
        const draftData = result.data.data || [];

        const processedProducts = draftData.map((product) => {
          const difference = product.stockOpnameQuantity - product.quantity;
          return {
            productCode: product.productCode,
            productName: product.productName,
            variantName: product.variantName,
            masterProductCode: product.masterProductCode,
            quantity: product.quantity || 0,
            trxPrice: product.trxPrice || 0,
            stockOpnameQuantity: product.stockOpnameQuantity || "",
            totalProductDiffrence:
              product.stockOpnameQuantity !== null ? difference : 0,
            stockOpnamePrice:
              product.stockOpnameQuantity !== null
                ? difference * product.trxPrice
                : 0,
            inventoryQuantity:
              product.inventoryQuantity || product.quantity || 0,
            id: product.id,
            stockOpnameId: product.stockOpnameId,
            stockOpnameType:
              parseInt(difference) > 0
                ? "STOCK OPNAME PLUS"
                : "STOCK OPNAME MINUS",
            stockOpnameTypeCode: parseInt(difference) > 0 ? 62 : 63,
          };
        });
        setStockOpnameProductList(processedProducts);
      } else {
        showError("Failed to load draft data");
      }
    } catch (error) {
      showError(error.message || "Error loading draft data");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle submit untuk finalisasi stock opname
  const handleSubmit = async () => {
    // Validasi form utama
    const formErrors = validateStockOpnameForm({
      branchSelected,
      dateStockOpname,
      stockOpnameProductList,
    });

    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    // Validasi perubahan produk
    const productValidation = validateProductChanges(stockOpnameProductList);
    if (!productValidation.valid) {
      showError(productValidation.message);
      return;
    }

    // Persiapkan data
    const formattedDate = moment(dateStockOpname).format("YYYY-MM-DD");
    const updatedProductList = prepareProductsForSave(stockOpnameProductList);
    const negativeDiffSummary = calculateNegativeDiffSummary(
      stockOpnameProductList
    );
    const positiveDiffSummary = calculatePositiveDiffSummary(
      stockOpnameProductList
    );
    const summaryData = calculateSummaryData(stockOpnameProductList);

    // Tambahkan ID draft ke data yang akan disimpan
    const stockOpnameData = {
      id: draftId, // ID draft untuk update
      dateStockOpname: formattedDate,
      branchSelected,
      totalTrxPrice: summaryData.totalTrxPrice,
      negativeDiffSummary,
      positiveDiffSummary,
      totalStockOpnameQuantity: summaryData.totalStockOpnameQuantity,
      totalStockOpnamePrice: summaryData.totalStockOpnamePrice,
      totalnventoryQuantity: summaryData.totalnventoryQuantity,
      totalTrxtInvetory: summaryData.totalTrxtInvetory,
      updatedProductList,
      notes,
      // Tambahkan status atau flag lain yang diperlukan
    };

    // Panggil API untuk finalisasi
    setIsSpinner(true);
    const result = await syncSaveStockOpname(
      stockOpnameData,
      stockOpnameParams
    );
    setIsSpinner(false);

    if (result.success) {
      showSuccess("Stock Opname finalized successfully");

      // Navigasi kembali ke daftar
      setTimeout(() => {
        navigate("/StockOpname");
      }, 300);
    } else {
      showError(result.error || "Failed to finalize Stock Opname");
    }
  };

  // Fungsi utilitas
  const showError = (msg) => {
    setIsOpen(true);
    setAlertType("error");
    setMessage(msg);
  };

  const showSuccess = (msg) => {
    setIsOpen(true);
    setAlertType("success");
    setMessage(msg);
  };

  return (
    <>
      <Spinner isShowSpinner={isSpinner || isLoading} />
      <CustomizedSnackbars
        isOpen={isOpen}
        typeInfo={alertType}
        messageInfo={message}
        handleCloseSnackbar={() => setIsOpen(false)}
      />

      <Paper sx={{p: 3, mb: 3, borderRadius: 1, boxShadow: 1}}>
        {/* Header */}
        <Box sx={{mb: 3}}>
          <Breadcrumbs separator="›" aria-label="breadcrumb">
            <Link
              color="inherit"
              onClick={() => navigate("/StockOpname")}
              sx={{cursor: "pointer", textDecoration: "none"}}
            >
              Stock Opname
            </Link>
            <Typography color="text.primary">Edit Stock Opname</Typography>
          </Breadcrumbs>

          <Typography variant="h6" sx={{mt: 1}}>
            Edit Stock Opname Draft
          </Typography>
        </Box>

        {/* Form Content */}
        <Grid container spacing={3}>
          {/* Branch Selection - filled based on stockOpnameParams */}
          <Grid item xs={12} sm={6}>
            <PickerSearch
              value={branchSelected}
              dataList={branchList}
              disabled={true} // Disabled pada mode edit
              getOptionLabel={(branch) => branch?.branchName || ""}
              labelPicker={"Branch / Outlet"}
              placeholderPicker={"branch / outlet"}
              sizePicker={"large"}
              isVisibleLabelPicker={true}
              error={!!errors.branchSelected}
              helperText={errors.branchSelected}
            />
          </Grid>

          {/* Date Selection - filled based on stockOpnameParams */}
          <Grid item xs={12} sm={6}>
            <FormDatePicker
              labelFormDatePicker="Date Stock Opname"
              formSize={"large"}
              dateValue={dateStockOpname}
              disabled={true} // Disabled pada mode edit
              error={!!errors.dateStockOpname}
              helperText={errors.dateStockOpname}
            />
          </Grid>

          {/* Notes */}
          <Grid item xs={12}>
            <FormInput
              label="Notes"
              name="notes"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              multiline
              rows={4}
            />
          </Grid>
        </Grid>

        {/* Product Table - sama seperti di AddStockOpname */}
        <Box sx={{mt: 4}}>
          <Typography variant="h6" gutterBottom>
            Products
          </Typography>

          {errors.products && (
            <Typography color="error" variant="body2" sx={{mb: 2}}>
              {errors.products}
            </Typography>
          )}

          <TableContainer>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  {tableHeadAddStockOpname.map((item) => (
                    <TableCell
                      key={item.name}
                      sx={{
                        fontWeight: "bold",
                        backgroundColor: "#060270",
                        color: "white",
                        width: item.size,
                      }}
                    >
                      {item.name}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={tableHeadAddStockOpname.length}
                      align="center"
                      sx={{py: 3}}
                    >
                      <CircularProgress size={30} />
                      <Typography variant="body2" sx={{mt: 1}}>
                        Loading products...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : stockOpnameProductList.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={tableHeadAddStockOpname.length}
                      align="center"
                      sx={{py: 4}}
                    >
                      <Typography
                        variant="body1"
                        sx={{color: "text.secondary"}}
                      >
                        No products found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  (rowsPerPageStockOpname > 0
                    ? stockOpnameProductList.slice(
                        pageStokOpname * rowsPerPageStockOpname,
                        pageStokOpname * rowsPerPageStockOpname +
                          rowsPerPageStockOpname
                      )
                    : stockOpnameProductList
                  ).map((item, index) => (
                    <TableRow key={item.productCode}>
                      <TableCell>{`${item.productName} - ${item.variantName}`}</TableCell>
                      <TableCell>{NumberFormat(0, item.quantity)}</TableCell>
                      <TableCell>{`Rp. ${NumberFormat(
                        0,
                        item.trxPrice
                      )}`}</TableCell>
                      <TableCell>
                        <FormInput
                          size="small"
                          label="Total Product Actual"
                          name="stockOpnameQuantity"
                          type="number"
                          value={parseInt(item.stockOpnameQuantity)}
                          onChange={(event) => {
                            handleQuantityChange(event, item.productCode);
                            calculateDifference(item.productCode);
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography
                          color={
                            item.totalProductDiffrence > 0
                              ? "success.main"
                              : item.totalProductDiffrence < 0
                              ? "error.main"
                              : "text.primary"
                          }
                        >
                          {NumberFormat(0, item.totalProductDiffrence)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          color={
                            item.stockOpnamePrice > 0
                              ? "success.main"
                              : item.stockOpnamePrice < 0
                              ? "error.main"
                              : "text.primary"
                          }
                        >
                          {`Rp. ${NumberFormat(0, item.stockOpnamePrice)}`}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
                {stockOpnameProductList.length > 0 && (
                  <TableRow sx={{"& > td": {borderBottom: "none"}}}>
                    <TableCell colSpan={4} />
                    <TableCell rowSpan={4}>
                      <Typography sx={{fontSize: "18px"}}>Total</Typography>
                    </TableCell>
                    <TableCell
                      sx={{fontWeight: "bold", fontSize: "18px"}}
                      align="left"
                    >
                      {`Rp. ${NumberFormat(0, calculateTotalPrice())}`}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {stockOpnameProductList.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[10, 25, 100]}
              component="div"
              count={stockOpnameProductList.length}
              rowsPerPage={rowsPerPageStockOpname}
              page={pageStokOpname}
              onPageChange={(event, newPage) => {
                setPageStockOpname(newPage);
              }}
              onRowsPerPageChange={(event) => {
                setRowsPerPageStockOpname(+event.target.value);
                setPageStockOpname(0);
              }}
            />
          )}
        </Box>

        {/* Action Buttons */}
        <Box sx={{mt: 4, display: "flex", justifyContent: "space-between"}}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/StockOpname")}
          >
            Back to Stock Opname
          </Button>

          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
          >
            Finalize Stock Opname
          </Button>
        </Box>
      </Paper>
    </>
  );
}

export default EditStockOpname;
